import { geoAlbersUsa } from "d3-geo";
import { FC, useCallback, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";

import { Box, Spinner } from "@/components/ui";

import {
  COUNTIES_URL,
  GeoFeature,
  geographyStyle,
  HatchDefs,
  MAP_HEIGHT,
  MAP_WIDTH,
  MapTooltip,
  SELECTED_STROKE,
  STATES_URL,
  TooltipState,
  uniqueHatches,
} from "./MapParts";
import type { Paint } from "./types";

/* ── Types ─────────────────────────────────────── */

export interface CountyMapProps {
  /** Paint per county FIPS; counties without one have no respondents. */
  paints: Record<string, Paint>;
  /** The state's shape, used to fit the projection. */
  stateFeature: GeoJSON.Feature | null;
  stateFips: string;
}

/* ── Constants ─────────────────────────────────── */

const PADDING = 16;
const EMPTY_TOOLTIP = "No respondents in this county yet";

/* ── Component ─────────────────────────────────── */

const CountyMap: FC<CountyMapProps> = ({ paints, stateFeature, stateFips }) => {
  const [loaded, setLoaded] = useState(false);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const hatches = useMemo(() => uniqueHatches(Object.values(paints)), [paints]);

  // Kept stable: react-simple-maps refetches a map file whenever
  // parseGeographies changes, and the tooltip re-renders on every move.
  const parseCounties = useCallback(
    (features: GeoJSON.Feature[]) => {
      setLoaded(true);
      return features.filter((f) => String(f.id).startsWith(stateFips));
    },
    [stateFips]
  );
  const parseOutline = useCallback(
    (features: GeoJSON.Feature[]) =>
      features.filter((f) => String(f.id) === stateFips),
    [stateFips]
  );

  // Fit the projection to the selected state (Alaska and Hawaii included).
  const projection = useMemo(
    () =>
      stateFeature
        ? geoAlbersUsa().fitExtent(
            [
              [PADDING, PADDING],
              [MAP_WIDTH - PADDING, MAP_HEIGHT - PADDING],
            ],
            stateFeature
          )
        : null,
    [stateFeature]
  );

  if (!projection) {
    return (
      <Box
        className="w-full items-center justify-center"
        style={{ aspectRatio: MAP_WIDTH / MAP_HEIGHT }}
      >
        <Spinner color="rgb(var(--color-primary-500))" size="small" />
      </Box>
    );
  }

  return (
    <Box
      style={{
        aspectRatio: MAP_WIDTH / MAP_HEIGHT,
        position: "relative",
        width: "100%",
      }}
    >
      {!loaded && (
        <Box className="absolute inset-0 items-center justify-center">
          <Spinner color="rgb(var(--color-primary-500))" size="small" />
        </Box>
      )}
      <ComposableMap
        height={MAP_HEIGHT}
        // @ts-expect-error react-simple-maps v3 uses a function projection as the d3 projection itself; its types expect a factory
        projection={projection}
        style={{ display: "block", height: "100%", width: "100%" }}
        width={MAP_WIDTH}
      >
        <HatchDefs hatches={hatches} />
        <Geographies geography={COUNTIES_URL} parseGeographies={parseCounties}>
          {({ geographies }: { geographies: GeoFeature[] }) =>
            geographies.map((geo) => {
              const paint = paints[geo.id];
              const show = (e: React.MouseEvent) =>
                setTooltip({
                  text: `${String(geo.properties?.name ?? "County")}\n${paint?.tooltip ?? EMPTY_TOOLTIP}`,
                  x: e.clientX,
                  y: e.clientY,
                });
              return (
                <Geography
                  geography={geo}
                  key={geo.rsmKey}
                  onMouseEnter={show}
                  onMouseLeave={() => setTooltip(null)}
                  onMouseMove={show}
                  style={geographyStyle(paint, false, false)}
                />
              );
            })
          }
        </Geographies>
        <Geographies geography={STATES_URL} parseGeographies={parseOutline}>
          {({ geographies }: { geographies: GeoFeature[] }) =>
            geographies.map((geo) => (
              <Geography
                geography={geo}
                key={geo.rsmKey}
                style={{
                  default: {
                    fill: "none",
                    outline: "none",
                    pointerEvents: "none",
                    stroke: SELECTED_STROKE,
                    strokeWidth: 1.25,
                  },
                  hover: {
                    fill: "none",
                    outline: "none",
                    pointerEvents: "none",
                  },
                  pressed: {
                    fill: "none",
                    outline: "none",
                    pointerEvents: "none",
                  },
                }}
              />
            ))
          }
        </Geographies>
      </ComposableMap>
      <MapTooltip tooltip={tooltip} />
    </Box>
  );
};

export default CountyMap;
